import React from 'react';

import { Box, Typography } from '@mui/material';
import { KeyIcon, ServerIcon } from '@sharedCR/coreDesignSystem/icons';
import { FilterTypes } from '@sharedCR/coreDesignSystem/Table/FilterBaseCDS/FilterBaseCDS.constants';
import {
  ActionCellButtonCDS,
  defaultActionCellButtonCDSProps,
} from '@sharedCR/coreDesignSystem/Table/TableCDS/cellContent';

import { getConditionalObject } from 'utils/data';

import AuditActorCell from './components/AuditActorCell';
import AuditExpandToggle from './components/AuditExpandToggle';
import AuditFieldsCell from './components/AuditFieldsCell';
import AuditOperationCell from './components/AuditOperationCell';
import {
  AUDIT_COLUMN_ID,
  AUDIT_COLUMN_WIDTH,
  DECORATED_AUDIT_COLUMN_WIDTH,
  HISTORY_EMPTY_MESSAGE,
  HISTORY_LOAD_FAILED_MESSAGE,
  HISTORY_LOADING_LABEL,
  HISTORY_UNAVAILABLE_MESSAGE,
  OPERATION_FILTER_SELECT_OPTIONS,
  TIMELINE_COLUMN_LABELS,
} from './EntityAuditHistory.constants';
import { EntityAuditHistoryTestIds } from './EntityAuditHistory.testIds';
import {
  ActorKind,
  formatRowDateTime,
  getActorFilterKey,
  getChangedFieldsFilterValue,
  getVisibleRowActions,
  toOperation,
} from './EntityAuditHistory.utils';

/** The non-person actor glyph; a Human actor uses an initials avatar instead. */
export const ACTOR_ICON = {
  [ActorKind.SYSTEM]: ServerIcon,
  [ActorKind.SERVICE]: KeyIcon,
};

/**
 * The SystemTable column config for the (flat) audit timeline — Time sorts, Change + Changed by
 * filter (multi-select), Fields changed filters (text/substring), and the Expand column toggles the
 * per-row drill-in. Row data is each summary plus `id` = auditId (so the detail renders under the
 * matching row). A consumer's `getRowDecoration` adds chips under the operation pill and a note to
 * the fields cell (and switches to the decorated column widths); its `renderRowActions` adds a
 * trailing row-actions menu, given the loaded rows and a way to open one of them.
 */
export const getAuditTableConfig = ({
  expandedRowId,
  onToggleExpand,
  actorNames,
  actorFilterOptions,
  getRowDecoration = null,
  renderRowActions = null,
  rowActionsContext = null,
}) => {
  const columnWidth = getRowDecoration ? DECORATED_AUDIT_COLUMN_WIDTH : AUDIT_COLUMN_WIDTH;

  return {
    [AUDIT_COLUMN_ID.EXPAND]: {
      label: '',
      width: columnWidth.EXPAND,
      align: 'center',
      content: row => (
        <AuditExpandToggle
          isExpanded={expandedRowId === row.id}
          rowId={row.id}
          onToggle={onToggleExpand}
        />
      ),
    },
    [AUDIT_COLUMN_ID.TIME]: {
      label: TIMELINE_COLUMN_LABELS.time,
      width: columnWidth.TIME,
      sort: true,
      content: row => (
        <Typography color="text.secondary" variant="regular14">
          {formatRowDateTime(row.occurredAtUtc)}
        </Typography>
      ),
    },
    [AUDIT_COLUMN_ID.OPERATION]: {
      label: TIMELINE_COLUMN_LABELS.operation,
      width: columnWidth.OPERATION,
      filter: {
        selectOptions: OPERATION_FILTER_SELECT_OPTIONS,
        filterType: FilterTypes.MULTI_SELECT,
      },
      filterAccessor: row => toOperation(row.operation),
      content: row => (
        <AuditOperationCell chips={getRowDecoration?.(row)?.chips ?? null} summary={row} />
      ),
    },
    [AUDIT_COLUMN_ID.ACTOR]: {
      label: TIMELINE_COLUMN_LABELS.actor,
      width: columnWidth.ACTOR,
      filter: {
        selectOptions: actorFilterOptions,
        filterType: FilterTypes.MULTI_SELECT,
      },
      filterAccessor: row => getActorFilterKey(row),
      content: row => <AuditActorCell actorNames={actorNames} summary={row} />,
    },
    [AUDIT_COLUMN_ID.FIELDS]: {
      label: TIMELINE_COLUMN_LABELS.changedFields,
      filter: true,
      filterAccessor: row => getChangedFieldsFilterValue(row),
      content: row => (
        <AuditFieldsCell decoration={getRowDecoration?.(row) ?? null} summary={row} />
      ),
    },
    ...getConditionalObject(!!renderRowActions, () => ({
      [AUDIT_COLUMN_ID.ACTIONS]: {
        ...defaultActionCellButtonCDSProps,
        content: row => (
          <Box data-testid={EntityAuditHistoryTestIds.ROW_ACTIONS}>
            <ActionCellButtonCDS
              items={getVisibleRowActions(renderRowActions(row, rowActionsContext))}
            />
          </Box>
        ),
      },
    })),
  };
};

const HISTORY_STATE_MESSAGE_DEFAULTS = { isLoading: false, hasRetry: false, retryTestId: null };

/**
 * The one timeline state message to show (loading / unavailable / failed + retry / empty), or null
 * when the timeline has rows to render. The states are mutually exclusive, in this order; `hasRetry`
 * marks the one that offers the re-read.
 */
export const getHistoryStateMessage = ({ isLoading, isUnavailable, hasFailed, isEmpty }) => {
  if (isLoading) {
    return {
      ...HISTORY_STATE_MESSAGE_DEFAULTS,
      isLoading: true,
      dataTestId: EntityAuditHistoryTestIds.LOADING,
      message: HISTORY_LOADING_LABEL,
    };
  }

  if (isUnavailable) {
    return {
      ...HISTORY_STATE_MESSAGE_DEFAULTS,
      dataTestId: EntityAuditHistoryTestIds.UNAVAILABLE,
      message: HISTORY_UNAVAILABLE_MESSAGE,
    };
  }

  if (hasFailed) {
    return {
      ...HISTORY_STATE_MESSAGE_DEFAULTS,
      dataTestId: EntityAuditHistoryTestIds.ERROR,
      message: HISTORY_LOAD_FAILED_MESSAGE,
      retryTestId: EntityAuditHistoryTestIds.RETRY_BUTTON,
      hasRetry: true,
    };
  }

  if (isEmpty) {
    return {
      ...HISTORY_STATE_MESSAGE_DEFAULTS,
      dataTestId: EntityAuditHistoryTestIds.EMPTY,
      message: HISTORY_EMPTY_MESSAGE,
    };
  }

  return null;
};
